/**
 * @file ConsentLiveCard.tsx
 * @description Карточка вида на экране «Согласие на cookie» (phase19.16,
 * Р3г; uiux-r3g-consent-texts §2): действующая редакция («На сайтах»),
 * заметка в две строки, охват «Получили N из M» (если API отдаёт),
 * [Смотреть], строка черновика браузера [Продолжить] [✕] и история.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { Info, X } from "lucide-react";
import { useId, type ReactNode } from "react";
import { Link } from "react-router";

import type { ConsentKindOverview } from "../../../hooks/platform/useConsentTextsOverview";
import type { ConsentTextsDraft } from "../../../lib/platform/consent-texts-draft";
import { draftLine, reachLine, revisionLine } from "../../../lib/platform/consent-texts-format";
import { Button } from "../../ui/button";
import { ConsentRevisionList } from "./ConsentRevisionList";

interface Props {
  kind: ConsentTextKind;
  title: string;
  overview: ConsentKindOverview;
  draft: ConsentTextsDraft | null;
  onView: (number: number) => void;
  onDropDraft: () => void;
  /** Кнопка «Новая редакция» в карточке (у баннера она в шапке страницы). */
  action?: ReactNode;
}

export function ConsentLiveCard({ kind, title, overview, draft, onView, onDropDraft, action }: Props) {
  const { current, history } = overview;
  const reach = reachLine(current?.shops);
  const older = history.filter((r) => r.number !== current?.number);
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className="flex min-w-0 flex-col gap-3 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]"
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <h2 id={headingId} className="type-section-title m-0 min-w-0 text-on-surface">
          {title}
        </h2>
        {action}
      </div>
      <div className="flex flex-col gap-4">
        {current ? (
          <div className="flex flex-col gap-2">
            <span className="type-caption text-on-surface-variant">На сайтах</span>
            <span className="type-body text-on-surface">{revisionLine(current)}</span>
            <p className="type-body m-0 line-clamp-2 break-words text-on-surface-variant">«{current.note}»</p>
            {reach ? (
              <p className="type-caption m-0 flex items-start gap-2 text-on-surface-variant">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                {reach} — остальные перейдут после «Обновить» и публикации вида сайта
              </p>
            ) : null}
            <Button variant="outline" className="min-h-12 self-start" onClick={() => onView(current.number)}>
              Смотреть
            </Button>
          </div>
        ) : (
          <p className="type-body m-0 text-on-surface-variant">
            Редакций нет — исходная редакция 1 не загружена. Сайты показывают встроенный текст редакции 1. Сообщите разработчику.
          </p>
        )}
        {draft ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-low px-3 py-2">
            <span className="type-body min-w-0 flex-1 text-on-surface">{draftLine(draft.authorName, draft.savedAt)}</span>
            <Button asChild variant="outline" className="min-h-12">
              <Link to={`/platform/consent-texts/new?kind=${kind}&draft=1`}>Продолжить</Link>
            </Button>
            <Button variant="ghost" className="min-h-12 min-w-12" aria-label="Удалить черновик" onClick={onDropDraft}>
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        ) : null}
        {older.length > 0 ? (
          <div className="flex flex-col gap-1">
            <h3 className="type-body m-0 font-semibold text-on-surface">Прежние редакции</h3>
            <ConsentRevisionList items={older} onView={onView} />
          </div>
        ) : null}
      </div>
    </section>
  );
}
